// src/components/ProductoAtributosEditor/ProductoAtributosEditor.js
import React, { useState, useEffect, useCallback } from 'react'
import { Card, Form, Badge, Alert, Spinner, Button, Collapse, Modal } from 'react-bootstrap'
import './ProductoAtributosEditor.css'

/**
 * Componente para configurar los valores de atributos disponibles para un producto
 * Soporta tanto atributos heredados de categorías como atributos directos
 * 
 * Props:
 * - atributos: Array de tipos de atributos (heredados + directos combinados)
 *   Estructura: [{ id, nombre, descripcion, activo, heredado, directo, valores: [{ id, nombre, activo, seleccionado }] }]
 * - valoresSeleccionados: Objeto con valores actualmente seleccionados { tipoId: [valorId, ...] }
 * - tiposDirectosIds: Array de IDs de tipos asignados directamente al producto
 * - tiposDisponibles: Array de todos los tipos disponibles para agregar (para selector)
 * - onChange: Función callback cuando cambian las selecciones de valores
 * - onTiposDirectosChange: Función callback cuando cambian los tipos directos
 * - disabled: Boolean para deshabilitar todo el componente
 * - loading: Boolean para mostrar estado de carga
 * - modo: 'editar' | 'ver' - En modo 'ver' solo muestra los valores seleccionados
 * - mostrarSelectorTipos: Boolean - Si debe mostrar el selector de tipos adicionales
 */
const ProductoAtributosEditor = ({
	atributos = [],
	valoresSeleccionados = {},
	tiposDirectosIds = [],
	tiposDisponibles = [],
	onChange,
	onTiposDirectosChange,
	disabled = false,
	loading = false,
	modo = 'editar',
	mostrarSelectorTipos = true
}) => {
	// Estado local para manejar las selecciones mientras el usuario edita
	const [selecciones, setSelecciones] = useState({})
	const [expandidos, setExpandidos] = useState({})
	const [mostrarModalAgregar, setMostrarModalAgregar] = useState(false)

	// Inicializar selecciones cuando cambian los valores externos
	useEffect(() => {
		setSelecciones(valoresSeleccionados || {})
	}, [valoresSeleccionados])

	// Expandir todos los tipos por defecto
	useEffect(() => {
		const inicial = {}
		atributos.forEach(tipo => {
			inicial[tipo.id] = true
		})
		setExpandidos(inicial)
	}, [atributos])

	// Manejar cambio de checkbox de un valor
	const handleValorChange = useCallback((tipoId, valorId, checked) => {
		setSelecciones(prev => {
			const nuevasSelecciones = { ...prev }
			const tipoIdStr = String(tipoId)
			const valorIdNum = Number(valorId)

			if (!nuevasSelecciones[tipoIdStr]) {
				nuevasSelecciones[tipoIdStr] = []
			}

			if (checked) {
				if (!nuevasSelecciones[tipoIdStr].includes(valorIdNum)) {
					nuevasSelecciones[tipoIdStr] = [...nuevasSelecciones[tipoIdStr], valorIdNum]
				}
			} else {
				nuevasSelecciones[tipoIdStr] = nuevasSelecciones[tipoIdStr].filter(id => id !== valorIdNum)
			}

			if (nuevasSelecciones[tipoIdStr].length === 0) {
				delete nuevasSelecciones[tipoIdStr]
			}

			if (onChange) {
				onChange(nuevasSelecciones)
			}

			return nuevasSelecciones
		})
	}, [onChange])

	// Seleccionar todos los valores de un tipo
	const handleSeleccionarTodos = useCallback((tipoId, valores) => {
		setSelecciones(prev => {
			const nuevasSelecciones = { ...prev }
			const tipoIdStr = String(tipoId)
			nuevasSelecciones[tipoIdStr] = valores
				.filter(v => v.activo)
				.map(v => Number(v.id))

			if (onChange) {
				onChange(nuevasSelecciones)
			}

			return nuevasSelecciones
		})
	}, [onChange])

	// Deseleccionar todos los valores de un tipo
	const handleDeseleccionarTodos = useCallback((tipoId) => {
		setSelecciones(prev => {
			const nuevasSelecciones = { ...prev }
			const tipoIdStr = String(tipoId)
			delete nuevasSelecciones[tipoIdStr]

			if (onChange) {
				onChange(nuevasSelecciones)
			}

			return nuevasSelecciones
		})
	}, [onChange])

	// Toggle expandir/colapsar un tipo
	const toggleExpandido = useCallback((tipoId) => {
		setExpandidos(prev => ({
			...prev,
			[tipoId]: !prev[tipoId]
		}))
	}, [])

	// Contar valores seleccionados de un tipo
	const contarSeleccionados = (tipoId) => {
		const tipoIdStr = String(tipoId)
		return selecciones[tipoIdStr]?.length || 0
	}

	// Verificar si un valor estÃ¡ seleccionado
	const estaSeleccionado = (tipoId, valorId) => {
		const tipoIdStr = String(tipoId)
		const valorIdNum = Number(valorId)
		return selecciones[tipoIdStr]?.includes(valorIdNum) || false
	}

	// Agregar un tipo directo
	const handleAgregarTipoDirecto = (tipoId) => {
		if (onTiposDirectosChange) {
			const nuevosIds = [...tiposDirectosIds, tipoId]
			onTiposDirectosChange(nuevosIds)
		}
		setMostrarModalAgregar(false)
	}

	// Quitar un tipo directo
	const handleQuitarTipoDirecto = (tipoId) => {
		if (onTiposDirectosChange) {
			const nuevosIds = tiposDirectosIds.filter(id => id !== tipoId)
			onTiposDirectosChange(nuevosIds)
		}
		// También quitar los valores seleccionados de ese tipo
		handleDeseleccionarTodos(tipoId)
	}

	// Obtener tipos disponibles para agregar (no heredados y no directos actuales)
	const tiposParaAgregar = tiposDisponibles.filter(tipo => {
		const yaEstaEnAtributos = atributos.some(a => a.id === tipo.id)
		return tipo.activo && !yaEstaEnAtributos
	})

	// Separar atributos heredados y directos
	const atributosHeredados = atributos.filter(a => a.heredado)
	const atributosDirectos = atributos.filter(a => a.directo && !a.heredado)

	// Si está cargando, mostrar spinner
	if (loading) {
		return (
			<div className="producto-atributos-loading text-center py-4">
				<Spinner animation="border" variant="primary" size="sm" />
				<span className="ms-2">Cargando atributos...</span>
			</div>
		)
	}

	// Modo ver: solo mostrar valores seleccionados
	if (modo === 'ver') {
		return (
			<div className="producto-atributos-ver">
				{atributos.map(tipo => {
					const tipoIdStr = String(tipo.id)
					const valoresDelTipo = selecciones[tipoIdStr] || []
					if (valoresDelTipo.length === 0) return null

					return (
						<div key={tipo.id} className="atributo-tipo-ver mb-3">
							<strong>
								{tipo.nombre}
								{tipo.directo && !tipo.heredado && (
									<Badge bg="info" className="ms-2" title="Atributo directo">
										<i className="fa fa-link"></i>
									</Badge>
								)}
								:
							</strong>
							<div className="valores-badges mt-1">
								{tipo.valores
									.filter(v => valoresDelTipo.includes(v.id))
									.map(valor => (
										<Badge
											key={valor.id}
											bg={valor.activo ? "primary" : "secondary"}
											className="me-1 mb-1"
										>
											{valor.nombre}
											{!valor.activo && <i className="fa fa-eye-slash ms-1" title="Valor inactivo"></i>}
										</Badge>
									))
								}
							</div>
						</div>
					)
				})}

				{Object.keys(selecciones).length === 0 && (
					<p className="text-muted mb-0">
						<i className="fa fa-minus me-1"></i>
						No hay valores de atributos configurados para este producto.
					</p>
				)}
			</div>
		)
	}

	// Modo editar
	const renderTipoCard = (tipo, esDirecto = false) => {
		const numSeleccionados = contarSeleccionados(tipo.id)
		const numValoresActivos = tipo.valores.filter(v => v.activo).length
		const todosSeleccionados = numSeleccionados === numValoresActivos && numValoresActivos > 0

		return (
			<Card
				key={tipo.id}
				className={`atributo-tipo-card mb-3 ${!tipo.activo ? 'tipo-inactivo' : ''} ${esDirecto ? 'tipo-directo' : 'tipo-heredado'}`}
			>
				<Card.Header
					className="d-flex justify-content-between align-items-center cursor-pointer"
					onClick={() => toggleExpandido(tipo.id)}
				>
					<div className="d-flex align-items-center">
						<i className={`fa fa-chevron-${expandidos[tipo.id] ? 'down' : 'right'} me-2`}></i>
						<strong>{tipo.nombre}</strong>
						{tipo.heredado && (
							<Badge bg="secondary" className="ms-2" title="Heredado de categorías">
								<i className="fa fa-sitemap me-1"></i>
								Heredado
							</Badge>
						)}
						{esDirecto && (
							<Badge bg="info" className="ms-2" title="Asignado directamente al producto">
								<i className="fa fa-link me-1"></i>
								Directo
							</Badge>
						)}
						{!tipo.activo && (
							<Badge bg="warning" text="dark" className="ms-2">Inactivo</Badge>
						)}
					</div>
					<div className="d-flex align-items-center">
						<Badge
							bg={numSeleccionados > 0 ? "success" : "secondary"}
							className="me-2"
						>
							{numSeleccionados} / {tipo.valores.length}
						</Badge>
						{esDirecto && (
							<Button
								variant="outline-danger"
								size="sm"
								onClick={(e) => {
									e.stopPropagation()
									handleQuitarTipoDirecto(tipo.id)
								}}
								title="Quitar este atributo del producto"
								disabled={disabled}
							>
								<i className="fa fa-times"></i>
							</Button>
						)}
					</div>
				</Card.Header>

				<Collapse in={expandidos[tipo.id]}>
					<div>
						<Card.Body>
							{tipo.descripcion && (
								<p className="text-muted small mb-3">{tipo.descripcion}</p>
							)}

							{/* Botones de selección rápida */}
							<div className="mb-3 d-flex gap-2">
								<Button
									variant="outline-primary"
									size="sm"
									onClick={() => handleSeleccionarTodos(tipo.id, tipo.valores)}
									disabled={disabled || todosSeleccionados}
								>
									<i className="fa fa-check-double me-1"></i>
									Seleccionar todos
								</Button>
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={() => handleDeseleccionarTodos(tipo.id)}
									disabled={disabled || numSeleccionados === 0}
								>
									<i className="fa fa-times me-1"></i>
									Quitar todos
								</Button>
							</div>

							{/* Lista de valores */}
							<div className="valores-grid">
								{tipo.valores.map(valor => (
									<Form.Check
										key={valor.id}
										type="checkbox"
										id={`attr-${tipo.id}-${valor.id}`}
										className={`valor-checkbox ${!valor.activo ? 'valor-inactivo' : ''}`}
										label={
											<span>
												{valor.nombre}
												{!valor.activo && (
													<i
														className="fa fa-eye-slash ms-1 text-muted"
														title="Valor inactivo - no visible en web"
													></i>
												)}
											</span>
										}
										checked={estaSeleccionado(tipo.id, valor.id)}
										onChange={(e) => handleValorChange(tipo.id, valor.id, e.target.checked)}
										disabled={disabled || (!valor.activo && !estaSeleccionado(tipo.id, valor.id))}
									/>
								))}
							</div>

							{tipo.valores.length === 0 && (
								<p className="text-muted mb-0">
									Este tipo de atributo no tiene valores definidos.
								</p>
							)}
						</Card.Body>
					</div>
				</Collapse>
			</Card>
		)
	}

	return (
		<div className="producto-atributos-editor">
			{/* Sección: Atributos heredados */}
			{atributosHeredados.length > 0 && (
				<div className="seccion-atributos mb-4">
					<h6 className="seccion-titulo text-muted mb-3">
						<i className="fa fa-sitemap me-2"></i>
						Heredados de categorÃ­as
					</h6>
					{atributosHeredados.map(tipo => renderTipoCard(tipo, false))}
				</div>
			)}

			{/* Sección: Atributos directos */}
			{atributosDirectos.length > 0 && (
				<div className="seccion-atributos mb-4">
					<h6 className="seccion-titulo text-info mb-3">
						<i className="fa fa-link me-2"></i>
						Atributos adicionales del producto
					</h6>
					{atributosDirectos.map(tipo => renderTipoCard(tipo, true))}
				</div>
			)}

			{/* Mensaje cuando no hay atributos */}
			{atributos.length === 0 && (
				<Alert variant="info" className="mb-3">
					<i className="fa fa-info-circle me-2"></i>
					Este producto no tiene atributos configurados.
					{mostrarSelectorTipos && tiposParaAgregar.length > 0 && (
						<span> Puedes agregar atributos adicionales usando el botón de abajo.</span>
					)}
				</Alert>
			)}

			{/* Botón para agregar tipos adicionales */}
			{mostrarSelectorTipos && tiposParaAgregar.length > 0 && (
				<div className="agregar-atributo-section">
					<Button
						variant="outline-primary"
						size="sm"
						onClick={() => setMostrarModalAgregar(true)}
						disabled={disabled}
					>
						<i className="fa fa-plus me-2"></i>
						Agregar atributo adicional
					</Button>
					<small className="text-muted ms-2">
						({tiposParaAgregar.length} disponible{tiposParaAgregar.length !== 1 ? 's' : ''})
					</small>
				</div>
			)}

			{/* Modal para agregar tipo */}
			<Modal show={mostrarModalAgregar} onHide={() => setMostrarModalAgregar(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>
						<i className="fa fa-plus me-2"></i>
						Agregar atributo al producto
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-muted mb-3">
						Selecciona un tipo de atributo para agregar directamente a este producto.
						Estos atributos son independientes de las categorías.
					</p>
					<div className="lista-tipos-agregar">
						{tiposParaAgregar.map(tipo => (
							<div
								key={tipo.id}
								className="tipo-agregar-item"
								onClick={() => handleAgregarTipoDirecto(tipo.id)}
							>
								<div className="tipo-info">
									<div className="tipo-nombre">{tipo.nombre}</div>
									{tipo.descripcion && (
										<div className="tipo-descripcion text-muted small">
											{tipo.descripcion}
										</div>
									)}
									<div className="tipo-valores-count text-muted small">
										{tipo.valores?.length || 0} valores disponibles
									</div>
								</div>
								<Button variant="outline-primary" size="sm">
									<i className="fa fa-plus"></i>
								</Button>
							</div>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setMostrarModalAgregar(false)}>
						Cancelar
					</Button>
				</Modal.Footer>
			</Modal>

			{/* Resumen de selección */}
			{Object.keys(selecciones).length > 0 && (
				<Alert variant="success" className="mt-3 mb-0">
					<i className="fa fa-check-circle me-2"></i>
					<strong>Configuración:</strong> {atributos.length} tipo(s) de atributo
					con {Object.values(selecciones).flat().length} valor(es) seleccionado(s).
				</Alert>
			)}
		</div>
	)
}

export default ProductoAtributosEditor